/**
 * SpiralTemplate — Behavioral Tension Cinema
 * ---------------------------------------------------------------
 * Linear escalation. Bubbles stack chronologically. Resonant
 * phrases isolate (surrounding bubbles dim). The spiral beat is
 * a quiet pivot, not a flash. Closes on a forensic end-frame.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import type { SimulationContent } from "@/lib/contentGenerator";
import { planTimeline, PALETTE, inResonantWindow } from "../utils/timing";
import { BackgroundField } from "../components/BackgroundField";
import { MessageBubble } from "../components/MessageBubble";
import { SpiralOverlay } from "../components/SpiralOverlay";
import { CTAFrame } from "../components/CTAFrame";

export const SpiralTemplate: React.FC<{ sim: SimulationContent }> = ({ sim }) => {
  const plan = planTimeline(sim);
  const frame = useCurrentFrame();
  const isolation = inResonantWindow(frame, plan.resonantFrames);

  // Restrained camera push — ambient, not aggressive
  const push = interpolate(frame, [0, plan.ctaStartFrame], [1.0, 1.025], {
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ backgroundColor: PALETTE.bg }}>
      <BackgroundField isolate={isolation.intensity} />

      <Sequence durationInFrames={plan.ctaStartFrame}>
        <AbsoluteFill
          style={{
            padding: "200px 80px 160px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            transform: `scale(${push})`,
            transformOrigin: "50% 60%",
          }}
        >
          {plan.turns.map((t, i) => {
            const visible = frame >= t.startFrame;
            const newestIdx = plan.turns.reduce(
              (acc, x, idx) => (frame >= x.startFrame ? idx : acc),
              -1
            );
            const stackOffset = Math.max(0, newestIdx - i);
            const isFocal =
              isolation.active && t.resonant && Math.abs(t.revealFrame - isolation.anchor) < 4;
            return (
              <MessageBubble
                key={t.index}
                turn={t}
                visible={visible}
                stackOffset={stackOffset}
                isolationActive={isolation.active}
                isFocal={isFocal}
              />
            );
          })}
        </AbsoluteFill>

        <SpiralOverlay spiralFrame={plan.spiralFrame} />
      </Sequence>

      <Sequence from={plan.ctaStartFrame} durationInFrames={plan.ctaFrames}>
        <CTAFrame preset="practice" />
      </Sequence>
    </AbsoluteFill>
  );
};
